import React, { useCallback, useState, useMemo, useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import { Popup } from 'devextreme-react/popup';
import { Form, Label, FormRef, GroupItem, SimpleItem, ButtonItem, EmptyItem, RequiredRule, EmailRule } from 'devextreme-react/form';
import { ButtonType } from 'devextreme-react/common';
import { formatMessage } from 'devextreme/localization';
import 'devextreme-react/text-area';

import LabelTemplate from '../label-template/LabelTemplate';

import CustomerApi from '../../api/CustomerApi';
import RoleGroupApi, { RoleGroupFilterMode } from '../../api/RoleGroupApi';

import UserInvitationDto from '../../classes/dtos/accountmodule/UserInvitationDto';
import RoleGroupDto from '../../classes/dtos/rolegroupmodule/RoleGroupDto';

import { useAuth } from '../../contexts/auth';


interface UserInvitationFormProps {
    Invited(): void;
    WorkspaceUser: boolean;
}

export interface UserInvitationFormHandle {
    Open: () => void;
}

const userInvitationDto = new UserInvitationDto(null);

const UserInvitationForm = forwardRef<UserInvitationFormHandle, UserInvitationFormProps>(({ Invited, WorkspaceUser }, ref) => {

    const formRef = useRef<FormRef>(null);

    const { sessionDetail } = useAuth();

    const [isOpen, setIsOpen] = useState(false);
    const [roleGroups, setRoleGroups] = useState<Array<RoleGroupDto> | null>(null);

    const title = WorkspaceUser
        ? formatMessage("UserInvitationForm_TitleWorkspace", sessionDetail?.WorkspaceName ?? "")
        : formatMessage(sessionDetail?.EnterpriseMode ? "UserInvitationForm_TitleGlobal" : "UserInvitationForm_Title");

    useImperativeHandle(ref, () => ({
        Open() {
            if (formRef.current !== null) {
                const formInstance = formRef.current.instance();
                formInstance.clear();
            }
            setIsOpen(true);
        }
    }));

    useEffect(() => {
        const filterMode = WorkspaceUser ? RoleGroupFilterMode.Workspace : RoleGroupFilterMode.WithoutWorkspace;

        RoleGroupApi.GetRoleGroupAsync(filterMode).then(result => {
            if (result.IsSuccess()) {
                setRoleGroups(result.Result);
            }
        });
    }, [WorkspaceUser]);

    const emailEditorOptions = {
        valueChangeEvent: 'keyup'
    };

    const roleGroupEditorOptions = {
        dataSource: roleGroups ?? [],
        valueExpr: 'Id',
        displayExpr: 'Name',
        placeholder: 'Select a group...'
    };

    const closeDialog = () => {
        setIsOpen(false);
    };

    const popupAttributes = useMemo(() => {
        return {
            id: 'elementId',
            class: 'class-name-popup'
        }
    }, []);

    const handleSubmit = useCallback(async (e: { preventDefault: () => void; }) => {
        e.preventDefault();
        let inviteResult = await CustomerApi.InviteUserAsync(userInvitationDto.Email ?? "", userInvitationDto.RoleGroupId!);
        if (inviteResult.IsFailed()) {
            alert(inviteResult.ErrorMessage);
            return;
        }
        Invited();
        setIsOpen(false);
    }, []);

    const inviteButtonOptions = {
        text: 'Send invitation',
        type: 'default' as ButtonType,
        useSubmitBehavior: true,
        width: '100%',
        icon: 'send'
    };

    return (
        <Popup
            wrapperAttr={popupAttributes}
            visible={isOpen}
            onHiding={closeDialog}
            dragEnabled={false}
            hideOnOutsideClick={false}
            showCloseButton={true}
            showTitle={true}
            title={title}
            container=".dx-viewport"
            width={600}
            height={420}
        >
            <form onSubmit={handleSubmit}>
                <div style={{ width: 480, color: '#9393A2', fontSize: 16, fontFamily: 'Manrope', fontWeight: '500', lineHeight: '24px', letterSpacing: 0.32, wordWrap: 'break-word' }}>To go further, please enter a valid email.</div>
                <Form
                    ref={formRef}
                    formData={userInvitationDto}
                    showColonAfterLabel={true}
                    showValidationSummary={false}
                    validationGroup="pathCreationDtoData"
                >
                    <GroupItem>
                        <SimpleItem dataField="Email" editorType="dxTextBox" editorOptions={emailEditorOptions} >
                            <Label render={LabelTemplate('rename')} />
                            <RequiredRule message="Email is required." />
                            <EmailRule message="Email is invalid" />
                        </SimpleItem>
                        <SimpleItem dataField="RoleGroupId" editorType="dxSelectBox" editorOptions={roleGroupEditorOptions} >
                            <Label text="Group" render={LabelTemplate('group')} />
                            <RequiredRule message="Group is required." />
                        </SimpleItem>
                    </GroupItem>
                    <EmptyItem colSpan={2} />
                    <GroupItem>
                        <ButtonItem buttonOptions={inviteButtonOptions} />
                    </GroupItem>
                </Form>
            </form>
        </Popup>
    );
});

export default UserInvitationForm;